import type { FC, ReactNode } from "react";
import type { LoaderFunction, MetaFunction } from "react-router";
import { data as responseData } from "react-router";
import type { ObjectSchema } from "yup";
import { object, string } from "yup";

import { useClientContext } from "~/contexts/client-context";
import { getLocalizedPromotionById } from "~/core/deprecated/promotions";
import { getBreadcrumbsHomeLabel } from "~/core/transformers/breadcrumb-list.transformer";
import { prepareSeoData } from "~/core/transformers/seo.transformer";
import { PromoDescription } from "~/features/promo-description/PromoDescription";
import { DetailedPromo } from "~/features-deprecated/detailed-promo/DetailedPromo";
import {
  featureFlagsContext,
  sdkContext,
  settingsContext,
} from "~/server/get-load-context";
import {
  createLcpImageLinkHeaders,
  resolveLcpImage,
} from "~/server/resolve-lcp-image.server";
import { STATIC_PAGE_CACHE_HEADERS } from "~/shared/cache-headers";
import { usePlayNowUrl } from "~/shared/utils/aff-integration";
import { getOriginalUrl } from "~/shared/utils/get-original-url";
import { useGtmContext } from "~/shared/utils/gtm-sdk/provider";
import { PageConstructor } from "~/widgets/page-constructor";
import { loadPagesByConstructor } from "~/widgets/pages-by-constructor/page-by-constructor.server";

import type { Route } from "./+types/$lang.promotions.$id";

const createDetailedPromoValidator = (): ObjectSchema<{
  id: string;
  lang: string;
}> => {
  return object({
    id: string().required(),
    lang: string().required(),
  });
};

export const loader = (async ({ params, context, request }) => {
  const featureFlags = context.get(featureFlagsContext);
  const sdk = context.get(sdkContext);
  const settings = context.get(settingsContext);
  const { id, lang } = createDetailedPromoValidator().validateSync(params);
  const isPromotionConstructorPageEnabled = featureFlags.getItem(
    "promotionPageByConstructor",
  ).enabled;

  const pageData =
    params.id && isPromotionConstructorPageEnabled
      ? await loadPagesByConstructor(sdk, "promotions", lang, {
          slug: id,
          segment: "promotions",
          pageList: "whenRelatedPages",
        })
      : undefined;

  if (!featureFlags.getItem("fullPromotionsPage").enabled) {
    throw new Response("Not Found", { status: 404, statusText: "Not Found" });
  }
  if (
    params.id === "cryptobonus" &&
    !featureFlags.getItem("featuresCryptoBonusPage").enabled
  ) {
    throw new Response("Not Found", { status: 404, statusText: "Not Found" });
  }

  try {
    const promotion =
      isPromotionConstructorPageEnabled && pageData?.page
        ? undefined
        : getLocalizedPromotionById(id, lang);

    const url = new URL(request.url);
    const cmsPath = `${url.protocol}//${url.host}`;
    const lcpImage = resolveLcpImage(pageData?.page, cmsPath);
    const responseHeaders = new Headers(STATIC_PAGE_CACHE_HEADERS);

    for (const linkHeader of createLcpImageLinkHeaders(lcpImage)) {
      responseHeaders.append("Link", linkHeader);
    }

    return responseData(
      {
        promotion,
        pageData: pageData?.page,
        pages: pageData?.pages,
        originalUrl: getOriginalUrl(request.url),
        languages: settings?.languages,
        lcpImage,
      },
      {
        headers: responseHeaders,
      },
    );
  } catch {
    throw new Response("Not Found", {
      status: 404,
      statusText: "Promotion not found",
    });
  }
}) satisfies LoaderFunction;

export function headers({
  loaderHeaders,
  actionHeaders,
}: Route.HeadersArgs): Headers {
  return loaderHeaders ?? actionHeaders;
}

export const meta: MetaFunction<typeof loader> = ({
  data,
  params,
  matches,
}) => {
  return prepareSeoData({
    pageSeo: data?.pageData?.page.seo,
    fallbackSeo: data?.pageData?.fallbackSeo?.seo,
    originalUrl: `${data?.originalUrl}`,
    currentLang: params?.lang ?? "",
    languages: data?.languages ?? [],
    matches,
    breadcrumbs: data?.pageData
      ? {
          data: data.pageData,
          homeLabel: getBreadcrumbsHomeLabel(matches),
        }
      : undefined,
  });
};

const Page: FC<Route.ComponentProps> = function ({
  loaderData: { promotion, pageData, pages },
}): ReactNode {
  const { playerID, language } = useClientContext();
  const { gtm } = useGtmContext();
  const playNowUrl = usePlayNowUrl();

  return (
    <>
      {pageData ? (
        <PageConstructor data={pageData} pages={pages} />
      ) : (
        <>
          <DetailedPromo
            promotion={promotion}
            playUrl={playNowUrl}
            analytics={{
              createAccountMain() {
                gtm.createAccountMain({
                  eventTime: new Date(),
                  playerID,
                  screenName: "mainPage",
                });
              },
              logInMain() {
                gtm.logInMain({
                  eventTime: new Date(),
                  playerID,
                  screenName: "mainPage",
                });
              },
            }}
          />

          {promotion?.cards?.map((card, index) => (
            <PromoDescription
              key={index}
              reverse={index % 2 === 1}
              alignment="center"
              data={card}
              lang={language}
            />
          ))}
        </>
      )}
    </>
  );
};

export default Page;
